import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { Consts } from '@/configs/consts'

export interface PollingOptions<T> {
  queryKey: string[]
  condition: (data: T) => boolean
  maxAttempts?: number
  intervalMs?: number
  waitAfterInvalidateMs?: number
  logContext?: string
}

export function usePollingQuery() {
  const queryClient = useQueryClient()

  const pollUntilCondition = useCallback(
    async <T>(options: PollingOptions<T>): Promise<boolean> => {
      const {
        queryKey,
        condition,
        maxAttempts = Consts.polling.maxAttempts,
        intervalMs = Consts.polling.intervalMs,
        waitAfterInvalidateMs = 200,
        logContext = 'polling',
      } = options

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          // Invalidate to trigger refetch
          await queryClient.invalidateQueries({ queryKey })

          // Wait for refetch to complete
          await new Promise(resolve => setTimeout(resolve, waitAfterInvalidateMs))

          // Get fresh data from cache
          const updatedData = queryClient.getQueryData(queryKey) as T

          if (!updatedData) {
            console.warn(`${logContext} attempt ${attempt}: No data available in cache`)

            if (attempt < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, intervalMs))
            }
            continue
          }

          // Check if condition is met
          if (condition(updatedData)) {
            return true // Condition successfully met
          }

          if (attempt < maxAttempts) {
            // Wait before next polling attempt
            await new Promise(resolve => setTimeout(resolve, intervalMs))
          }
        } catch (error) {
          console.error(`${logContext} attempt ${attempt} failed:`, error)
          if (attempt === maxAttempts) {
            return false // Final attempt failed
          }
        }
      }

      return false // Polling timeout
    },
    [queryClient],
  )

  return { pollUntilCondition }
}
